/**
 * Layer 0 - matching: which written note does each press belong to?
 *
 * Two rules, settable per region of a score:
 * - Time-window: a press counts for a note whose binding window is open at that moment.
 * - Order: a press counts for the next unmatched note (the earliest unmatched slice of the
 *   region); timing is ignored, so onset and offset cannot be graded there.
 *
 * Every candidate is ranked the same way, so a press always binds deterministically:
 *   1. nearest pitch (pitch distance is ignored when pitch is not judged);
 *   2. a note not yet matched before one already matched;
 *   3. the note whose notated onset is nearer the press;
 *   4. the lower note;
 *   5. score order.
 * If the winner is unmatched, the press binds to it (as a wrong pitch when the pitch differs).
 * If the winner is already matched, the press is an extra note charged to it. A press with no
 * candidate at all is an extra note charged to the note sounding at that moment, or, between
 * notes, to the nearest note in time; outside the piece it is stray and never charged.
 */

import { Clock } from './clock';
import { ScoreNote } from './types';

export type MatchingRule = 'time-window' | 'order';

/**
 * How extra notes (presses that match nothing) count:
 * - not at all;
 * - `capped`: charged to the held note once - its credit can fall no lower than `credit`
 *   however many extras land on it;
 * - `compounding`: every extra multiplies the held note's credit by `credit` again;
 * - `own-error`: every extra is a full error (credit 0) in its own right.
 */
export type ExtraNoteRule =
  | { readonly counts: false }
  | { readonly counts: true; readonly charge: 'capped' | 'compounding'; readonly credit: number }
  | { readonly counts: true; readonly charge: 'own-error' };

export interface MatchingConfig {
  /** The rule for notes outside any named region. */
  readonly rule: MatchingRule;
  /** Rules for named regions, e.g. a loosely graded trill under order matching. */
  readonly regionRules?: Readonly<Record<string, MatchingRule>>;
  readonly extraNotes: ExtraNoteRule;
}

/**
 * The binding window: how far from its notated onset a press may still bind to a note. An
 * engine constant, not a setting - graded tolerances live in the credit curves inside it.
 * Tempo-relative, in beats.
 */
export const BINDING_WINDOW_BEATS = { early: 0.35, late: 0.6 } as const;

/** A repeat of the same key at the same velocity this soon after the last press is key chatter. */
export const KEY_CHATTER_MS = 10;

/** The matching rule a note falls under. */
export function ruleForNote(config: MatchingConfig, note: ScoreNote): MatchingRule {
  const regionRule =
    note.region !== undefined && config.regionRules ? config.regionRules[note.region] : undefined;
  return regionRule ?? config.rule;
}

export interface BindingWindow {
  readonly dueMs: number;
  readonly startMs: number;
  readonly endMs: number;
}

export function bindingWindow(clock: Clock, note: ScoreNote): BindingWindow {
  const dueMs = clock.msAtBeat(note.onsetBeat);
  const msPerBeat = clock.msPerBeatAt(note.onsetBeat);
  return {
    dueMs,
    startMs: dueMs - BINDING_WINDOW_BEATS.early * msPerBeat,
    endMs: dueMs + BINDING_WINDOW_BEATS.late * msPerBeat,
  };
}

/** What the ranking needs to know about one candidate note. */
export interface Candidate {
  /** Index of the note in score order. */
  readonly index: number;
  readonly pitch: number;
  readonly matched: boolean;
  /** Notated onset time, or null under order matching (no clock). */
  readonly dueMs: number | null;
}

export interface PressQuery {
  readonly timeMs: number;
  readonly pitch: number;
  /** False when pitch is not judged: then any key counts and pitch distance is ignored. */
  readonly pitchMatters: boolean;
}

type RankKey = readonly [number, number, number, number, number];

function rankKey(press: PressQuery, candidate: Candidate): RankKey {
  return [
    press.pitchMatters ? Math.abs(press.pitch - candidate.pitch) : 0,
    candidate.matched ? 1 : 0,
    candidate.dueMs === null ? 0 : Math.abs(press.timeMs - candidate.dueMs),
    candidate.pitch,
    candidate.index,
  ];
}

function compareKeys(a: RankKey, b: RankKey): number {
  const difference = a.map((value, position) => value - b[position]).find((delta) => delta !== 0);
  return difference ?? 0;
}

/** The candidate a press goes to, by the ranking above; null when there is none. */
export function rankCandidates(
  press: PressQuery,
  candidates: readonly Candidate[],
): Candidate | null {
  return candidates.reduce<Candidate | null>((best, candidate) => {
    if (best === null) {
      return candidate;
    }
    return compareKeys(rankKey(press, candidate), rankKey(press, best)) < 0 ? candidate : best;
  }, null);
}
